import React, { useEffect, useState } from 'react';
import { supabase, Profile, Certification, InsurancePolicy } from '../../lib/supabase';
import { TradeBadge } from './TradeBadge';
import {
  User,
  MapPin,
  Phone,
  Mail,
  Briefcase,
  Award,
  CheckCircle2,
  Calendar,
  FileText,
  Shield,
  Building2,
  Download,
  Share2,
  ExternalLink
} from 'lucide-react';
import { generateVCard, downloadVCard, shareViaWebShare, copyToClipboard, generateProfileURL } from '../../lib/shareService';

interface PublicProfileViewProps {
  slug: string;
}

export const PublicProfileView: React.FC<PublicProfileViewProps> = ({ slug }) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [insurancePolicies, setInsurancePolicies] = useState<InsurancePolicy[]>([]);
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  useEffect(() => {
    loadPublicProfile();
  }, [slug]);

  const loadPublicProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('public_slug', slug)
        .eq('public_profile_enabled', true)
        .eq('role', 'contractor')
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profileData) {
        setError('Profile not found or not available');
        return;
      }

      setProfile(profileData);

      const { data: authData } = await supabase.auth.admin.getUserById(profileData.id);
      if (authData?.user?.email) {
        setEmail(authData.user.email);
      }

      const { data: certsData } = await supabase
        .from('certifications')
        .select('*')
        .eq('user_id', profileData.id)
        .order('created_at', { ascending: false });

      setCertifications(certsData || []);

      const { data: insuranceData } = await supabase
        .from('insurance_policies')
        .select('*')
        .eq('user_id', profileData.id)
        .order('expiry_date', { ascending: false });

      setInsurancePolicies(insuranceData || []);

      await supabase.rpc('increment_profile_views', { slug });

    } catch (error) {
      console.error('Error loading public profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVCard = () => {
    if (!profile) return;
    downloadVCard({
      profile,
      certifications,
      insurancePolicies,
      email
    });
  };

  const handleShare = async () => {
    if (!profile) return;

    const url = generateProfileURL(slug);
    const shareData = {
      title: `${profile.full_name} - Professional Contractor`,
      text: `Check out ${profile.full_name}'s professional contractor profile`,
      url
    };

    const shared = await shareViaWebShare(shareData);
    if (!shared) {
      await copyToClipboard(url);
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 3000);
    }
  };

  const activeCerts = certifications.filter(cert => {
    if (!cert.expiry_date) return true;
    return new Date(cert.expiry_date) > new Date();
  });

  const activeInsurance = insurancePolicies.filter(policy =>
    policy.status === 'active' || policy.status === 'expiring'
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Profile Not Found</h1>
          <p className="text-slate-600">{error || 'This profile is not available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Building2 className="w-8 h-8 text-slate-900" />
              <span className="text-xl font-bold text-slate-900">VendorBoom</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownloadVCard}
                className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Save Contact</span>
              </button>
              <button
                onClick={handleShare}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center space-x-2"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showCopySuccess && (
        <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 z-50">
          <CheckCircle2 className="w-5 h-5" />
          <span>Link copied to clipboard!</span>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="h-40 bg-gradient-to-r from-slate-700 to-slate-900"></div>
          <div className="px-6 pb-6 -mt-20">
            <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-6">
              <div className="w-32 h-32 rounded-xl bg-white border-4 border-white shadow-lg flex items-center justify-center">
                <User className="w-16 h-16 text-slate-400" />
              </div>

              <div className="flex-1 mt-4 sm:mt-0 sm:mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900">{profile.full_name}</h1>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {profile.trades && profile.trades.length > 0 ? (
                        profile.trades.map((trade) => (
                          <TradeBadge key={trade} trade={trade} size="md" />
                        ))
                      ) : (
                        <span className="text-slate-600">General Contractor</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 sm:mt-0">
                    {profile.verification_status === 'verified' ? (
                      <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full border border-green-200">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm font-medium">Verified</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full border border-slate-200">
                        <span className="text-sm font-medium">Contractor</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-slate-600">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="w-4 h-4" />
                    <span className="font-medium">{profile.jobs_completed}</span>
                    <span>jobs completed</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4" />
                    <span className="font-medium">{profile.rating.toFixed(1)}</span>
                    <span>rating</span>
                  </div>
                  {profile.profile_views !== undefined && profile.profile_views > 0 && (
                    <div className="flex items-center space-x-2">
                      <ExternalLink className="w-4 h-4" />
                      <span className="font-medium">{profile.profile_views}</span>
                      <span>profile views</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            {profile.bio && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-3">About</h2>
                <p className="text-slate-600 whitespace-pre-wrap">{profile.bio}</p>
              </div>
            )}

            {activeCerts.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Certifications & Licenses</h2>
                <div className="space-y-3">
                  {activeCerts.map((cert) => (
                    <div key={cert.id} className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-medium text-slate-900">{cert.name}</h3>
                          {cert.verified && (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                        {cert.issuer && (
                          <p className="text-sm text-slate-600">{cert.issuer}</p>
                        )}
                        {cert.expiry_date && (
                          <div className="flex items-center text-xs text-slate-500 mt-1">
                            <Calendar className="w-3 h-3 mr-1" />
                            Expires {new Date(cert.expiry_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                        cert.type === 'license'
                          ? 'bg-blue-100 text-blue-700'
                          : cert.type === 'certification'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {cert.type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeInsurance.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Insurance Coverage</h2>
                <div className="space-y-3">
                  {activeInsurance.map((policy) => (
                    <div key={policy.id} className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <Shield className="w-4 h-4 text-slate-600" />
                          <h3 className="font-medium text-slate-900 capitalize">
                            {policy.policy_type.replace('_', ' ')} Insurance
                          </h3>
                        </div>
                        <p className="text-sm text-slate-600">{policy.provider}</p>
                        {policy.coverage_amount && (
                          <p className="text-sm text-slate-600 mt-1">
                            Coverage: ${policy.coverage_amount.toLocaleString()}
                          </p>
                        )}
                        <div className="flex items-center text-xs text-slate-500 mt-1">
                          <Calendar className="w-3 h-3 mr-1" />
                          Valid until {new Date(policy.expiry_date).toLocaleDateString()}
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        policy.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {policy.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeCerts.length === 0 && activeInsurance.length === 0 && !profile.bio && (
              <div className="bg-white rounded-xl border border-slate-200 p-12">
                <div className="text-center text-slate-500">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p>No additional information available</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-900 mb-4">Contact Information</h2>
              <div className="space-y-3">
                {email && (
                  <div className="flex items-start">
                    <Mail className="w-4 h-4 text-slate-400 mt-1 mr-3" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-500 mb-1">Email</div>
                      <a
                        href={`mailto:${email}`}
                        className="text-sm text-slate-900 hover:text-slate-700 truncate block"
                      >
                        {email}
                      </a>
                    </div>
                  </div>
                )}

                {profile.phone && (
                  <div className="flex items-start">
                    <Phone className="w-4 h-4 text-slate-400 mt-1 mr-3" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-500 mb-1">Phone</div>
                      <a
                        href={`tel:${profile.phone}`}
                        className="text-sm text-slate-900 hover:text-slate-700"
                      >
                        {profile.phone}
                      </a>
                    </div>
                  </div>
                )}

                {profile.location && (
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 text-slate-400 mt-1 mr-3" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-500 mb-1">Location</div>
                      <div className="text-sm text-slate-900">{profile.location}</div>
                    </div>
                  </div>
                )}

                {profile.company_name && (
                  <div className="flex items-start">
                    <Briefcase className="w-4 h-4 text-slate-400 mt-1 mr-3" />
                    <div className="flex-1">
                      <div className="text-xs text-slate-500 mb-1">Company</div>
                      <div className="text-sm text-slate-900">{profile.company_name}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-slate-900 rounded-xl p-6 text-white">
              <h3 className="font-semibold mb-2">Download Contact Card</h3>
              <p className="text-sm text-slate-300 mb-4">
                Save this contractor's information to your device
              </p>
              <button
                onClick={handleDownloadVCard}
                className="w-full bg-white text-slate-900 font-medium py-2 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download vCard</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Powered by VendorBoom</p>
        </div>
      </div>
    </div>
  );
};
